/*
 * Transient confirmations — reported 17 Aug 2026: *"when we click on save it should show some
 * message of confirmation… as of now, not able to understand what happened."*
 *
 * The report was about the interview plan, but the gap is product-wide. Every mutating form in the
 * app follows the same pattern — a `useMutation` whose `onSuccess` closes an editor and invalidates
 * a query — and the *only* feedback that produces is a brief spinner on the button and a list that
 * redraws with the values already in it. When somebody saves a plan they did not change, or saves a
 * default that looks identical to what was on screen a moment ago, **nothing observable happens at
 * all**. So they press it again.
 *
 * ── Why this and not an inline message ──────────────────────────────────────────────────────────
 *
 * `Banner` and `InlineAlert` already carry failures, and they should keep carrying them: an error
 * belongs beside the control that produced it, where it can be read at leisure and stays put while
 * the reader fixes the thing. A *success* is the opposite kind of message. It has no detail worth
 * studying, it is stale the moment it is read, and pinning it above a form leaves the screen with a
 * permanent "Saved" that says nothing about whether the *next* press worked.
 *
 * ── Two deliberate asymmetries ──────────────────────────────────────────────────────────────────
 *
 * **A confirmation dismisses itself; a failure does not.** `dwellMs` applies to every tone except
 * `danger`. A confirmation missed costs nothing — the thing was saved either way. A failure missed
 * costs the work, and a five-second window is exactly long enough to be looking somewhere else.
 *
 * **One region, `aria-live="polite"`.** Not assertive, and not one region per tone. Polite queues
 * behind whatever a screen reader is currently reading rather than cutting it off, which is right
 * for "Saved" and would be wrong for an error — but errors in this product are announced by the
 * `Banner` that renders in place, and two announcements of one failure is worse than one.
 *
 * Labels come in as props, exactly as `AppShell` takes `ShellLabels`. The design system holds no
 * catalogue and must not import one: FE-03 lives at the app boundary, and a package that shipped
 * English would put a user-facing string somewhere the i18n gate does not look.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * `positive` confirms, `neutral` reports something that merely happened, `danger` reports a failure
 * and stays until dismissed. Deliberately not the full `Severity` scale — a toast that needed four
 * levels of gradation would be a message that belongs on the page.
 */
export type ToastTone = 'positive' | 'neutral' | 'danger';

export interface ToastLabels {
  /** Names the live region for a screen reader listing landmarks. */
  readonly region: string;
  readonly dismiss: string;
}

export type Notify = (message: string, tone?: ToastTone) => void;

interface ToastRecord {
  readonly id: number;
  readonly message: string;
  readonly tone: ToastTone;
}

const ToastContext = createContext<Notify | null>(null);

export interface ToastProviderProps {
  readonly labels: ToastLabels;
  readonly children: ReactNode;
  /** How long a self-dismissing toast stays. Five seconds reads twice without loitering. */
  readonly dwellMs?: number;
}

export function ToastProvider({ labels, children, dwellMs = 5000 }: ToastProviderProps) {
  const [toasts, setToasts] = useState<readonly ToastRecord[]>([]);
  const nextId = useRef(0);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const notify = useCallback<Notify>(
    (message, tone = 'positive') => {
      nextId.current += 1;
      const id = nextId.current;
      setToasts((current) => [...current, { id, message, tone }]);

      if (tone !== 'danger') {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), dwellMs),
        );
      }
    },
    [dismiss, dwellMs],
  );

  /*
   * Every pending timer cleared on unmount. A `setTimeout` holding a closure over `setToasts` will
   * fire after the tree is gone and React will warn about it — and in a test runner it keeps the
   * process alive, which is how a suite that passes hangs for five seconds per assertion.
   */
  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach((timer) => clearTimeout(timer));
      pending.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={notify}>
      {children}
      {/*
        Rendered unconditionally, empty or not. A live region has to exist *before* the text
        arrives in it — one created at the moment of the announcement is a new node rather than a
        change to a watched one, and most screen readers say nothing at all.
      */}
      <div className="tf-toasts" role="status" aria-live="polite" aria-label={labels.region}>
        {toasts.map((toast) => (
          <div key={toast.id} className={`tf-toast tf-toast--${toast.tone}`}>
            <span className="tf-toast__message">{toast.message}</span>
            <button
              type="button"
              className="tf-toast__dismiss"
              aria-label={labels.dismiss}
              onClick={() => dismiss(toast.id)}
            >
              <span aria-hidden="true">✕</span>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * Throws rather than returning a no-op when no provider is above it.
 *
 * A silent no-op would mean a screen wired to confirm its saves and confirming nothing, which is
 * the exact bug this file exists to fix — and it would be invisible until somebody used the screen.
 */
export function useToast(): Notify {
  const notify = useContext(ToastContext);
  if (notify === null) {
    throw new Error('useToast was called outside a ToastProvider.');
  }
  return notify;
}
